<template>
  <EasyDataTable
    v-sortable="sortableOptions"
    ref="dataTable"
    v-model:server-options="serverOptions"
    :server-items-length="serverItemsLength"
    :headers="props.gridColumns"
    :items="props.items"
    :loading="props.loading"
    rows-per-page="500"
    hide-footer
    :fixed-header="true"
    :table-height="500"
    id="project-items-table"
    @click-row="rowClicked"
  >
    <template v-for="column in props.gridColumns" #[getColumnSlotName(column)]>
      {{ column.text }}
    </template>
    <template #item-model="item">
      <div
        class="inline-flex w-full pl-0 py-1"
        :class="{ 'item-selected': item.selected }"
      >
        <Bars2Icon
          @mousedown="item.grabbed = true"
          @mouseup="item.grabbed = false"
          class="w-5 mr-3 row_order_handle text-gray-500"
          :class="{ grabbed: item.grabbed }"
        />
        <Button
          button-color="primary"
          button-size="xs"
          class="mr-2 cursor-pointer !px-2"
          @click="emits('actions-view-clicked', item)"
          :data-tooltip-target="`view-button-tooltip-${item.id}`"
          data-tooltip-placement="bottom"
        >
          <template #content>
            <MagnifyingGlassIcon class="w-4" />
          </template>
        </Button>
        <div
          :id="`view-button-tooltip-${item.id}`"
          role="tooltip"
          class="text-xs absolute invisible z-50 inline-block px-2 py-1 font-medium text-gray-800 transition-opacity duration-300 bg-gray-200 rounded-lg opacity-0 shadow-sm tooltip"
        >
          View
          <div class="tooltip-arrow" data-popper-arrow></div>
        </div>
        <Button
          button-color="primary"
          button-size="xs"
          class="mr-2 cursor-pointer !px-2"
          @click="emits('actions-edit-clicked', item)"
          :data-tooltip-target="`edit-button-tooltip-${item.id}`"
          data-tooltip-placement="bottom"
        >
          <template #content>
            <CheckIcon v-if="item.editing" class="w-4" />
            <PencilIcon v-else class="w-4" />
          </template>
        </Button>
        <div
          :id="`edit-button-tooltip-${item.id}`"
          role="tooltip"
          class="text-xs absolute invisible z-50 inline-block px-2 py-1 font-medium text-gray-800 transition-opacity duration-300 bg-gray-200 rounded-lg opacity-0 shadow-sm tooltip"
        >
          {{ item.editing ? "Done" : "Edit" }}
          <div class="tooltip-arrow" data-popper-arrow></div>
        </div>
        <Button
          button-color="red"
          button-size="xs"
          class="mr-2 cursor-pointer !px-1"
          @click="emits('actions-delete-clicked', item)"
          :data-tooltip-target="`delete-button-tooltip-${item.id}`"
          data-tooltip-placement="bottom"
        >
          <template #content>
            <TrashIcon class="h-4 w-5 flex-shrink-0" aria-hidden="true" />
          </template>
        </Button>
        <div
          :id="`delete-button-tooltip-${item.id}`"
          role="tooltip"
          class="text-xs absolute invisible z-50 inline-block px-2 py-1 font-medium text-gray-800 transition-opacity duration-300 bg-gray-200 rounded-lg opacity-0 shadow-sm tooltip"
        >
          Delete
          <div class="tooltip-arrow" data-popper-arrow></div>
        </div>
        <label class="py-2 font-medium">
          {{ item.model }}
        </label>
      </div>
    </template>
    <template #item-reference="item">
      <div v-if="item.editing" class="inline-flex w-full pl-0">
        <Input
          input-class="text-xs"
          v-model="item.reference"
          @update:model-value="emits(`item-reference-changed`, item, $event)"
        />
      </div>
      <template v-else>
        {{ item.reference }}
      </template>
    </template>
    <template #item-quantity="item">
      <div v-if="item.editing" class="inline-flex w-full pl-0">
        <div class="grid grid-cols-8 gap-0">
          <div class="col-span-2">
            <Button
              button-size="sm"
              class="!py-1 !px-1 rounded-r-none w-full justify-center"
              button-color="primary"
              @click="emits('decrease-item-quantity', item)"
            >
              <template #content>
                <MinusIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              </template>
            </Button>
          </div>
          <div class="col-span-4">
            <Input
              input-type="number"
              v-model="item.quantity"
              class="px-0"
              input-class="!py-1 text-center !rounded-none"
              @update:model-value="emits('set-item-quantity', item, $event)"
            />
          </div>
          <div class="col-span-2">
            <Button
              button-size="sm"
              class="!py-1 !px-1 justify-center flex self-end rounded-l-none w-full"
              button-color="primary"
              @click="emits('increase-item-quantity', item)"
            >
              <template #content>
                <PlusIcon class="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              </template>
            </Button>
          </div>
        </div>
      </div>
      <template v-else>
        {{ item.quantity }}
      </template>
    </template>
    <template #item-cost="item">
      {{
        pricing.toCurrencyString(
          item.cost * projectCurrency.conversionRate,
          projectCurrency.value
        )
      }}
    </template>
    <template #item-fanprice="item">
      <div
        v-if="
          item.editing &&
          item.allowFanPricingOverride &&
          (user.userGroup.isAdmin ||
            user.userGroup.pricing.allowPricingOverride)
        "
        class="inline-flex w-full pl-0"
      >
        <Input
          input-type="number"
          v-model="item.fanPrice"
          class="px-0"
          input-class="!py-1 text-center !rounded-none"
          @update:model-value="emits('set-item-fan-price', item, $event)"
        />
      </div>
      <template v-else>
        {{ item.fanPrice }}
      </template>
    </template>
    <template #item-motorprice="item">
      <div
        v-if="
          item.editing &&
          item.allowMotorPricingOverride &&
          (user.userGroup.isAdmin ||
            user.userGroup.pricing.allowPricingOverride)
        "
        class="inline-flex w-full pl-0"
      >
        <Input
          input-type="number"
          v-model="item.motorPrice"
          class="px-0"
          input-class="!py-1 text-center !rounded-none"
          @update:model-value="emits('set-item-motor-price', item, $event)"
        />
      </div>
      <template v-else>
        {{ item.motorPrice }}
      </template>
    </template>
    <template #item-price="item">
      {{ getItemUnitPriceWithDiscount(item) }}
    </template>
    <template #item-totalprice="item">
      {{ getTotalItemPriceWithDiscount(item, item.quantity) }}
    </template>
    <template #expand="item">
      <div v-if="showAccessoriesTable(item)" class="pl-8">
        <EasyDataTable
          :headers="accessoryGridColumns"
          :items="itemAccessories(item)"
          rows-per-page="20"
          hide-footer
          class="accessories-table"
        >
          <template #item-reference="accessory">
            <div v-if="item.editing" class="inline-flex w-full pl-0">
              <Input
                @update:model-value="
                  emits(`accessory-reference-changed`, item, accessory, $event)
                "
                v-model="accessory.reference"
                input-class="text-xs"
              />
            </div>
            <template v-else>
              {{ accessory.reference }}
            </template>
          </template>
          <template #item-quantity="accessory">
            {{ item.quantity * accessory.quantity }}
          </template>
          <template #item-cost="accessory">
            {{
              pricing.toCurrencyString(
                accessory.cost * projectCurrency.conversionRate,
                projectCurrency.value
              )
            }}
          </template>
          <template #item-price="accessory">
            <div
              v-if="
                item.editing &&
                accessory.allowPricingOverride &&
                (user.userGroup.isAdmin ||
                  user.userGroup.pricing.allowPricingOverride)
              "
              class="inline-flex w-full pl-0"
            >
              <Input
                input-type="number"
                v-model="accessory.price"
                class="px-0"
                input-class="!py-1 text-center !rounded-none"
                @update:model-value="
                  emits('set-accessory-price', item, accessory, $event)
                "
              />
            </div>
            <template v-else>
              {{ getItemUnitPriceWithDiscount(accessory, project.value) }}
            </template>
          </template>
          <template #item-totalprice="accessory">
            {{
              getTotalItemPriceWithDiscount(
                accessory,
                item.quantity * accessory.quantity
              )
            }}
          </template>
        </EasyDataTable>
      </div>
    </template>
    <template #loading>
      <div role="status" class="">
        <svg
          class="inline mr-2 w-8 h-8 text-gray-200 animate-spin dark:text-gray-600 fill-primary-500"
          viewBox="0 0 100 101"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
            fill="currentColor"
          />
          <path
            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
            fill="currentFill"
          />
        </svg>
      </div>
    </template>
  </EasyDataTable>
</template>

<script setup>
import {
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowTopRightOnSquareIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpDownIcon,
  PlusIcon,
  MinusIcon,
  TrashIcon,
  Bars2Icon,
  MagnifyingGlassIcon,
  PencilIcon,
  CheckIcon,
} from "@heroicons/vue/20/solid";
import { reactive, computed, watch, ref, onMounted, nextTick } from "vue";
import Button from "../shared/Button.vue";
import Input from "../shared/Input.vue";
import { useProjectStore } from "../../stores/project";
import { storeToRefs } from "pinia";
import * as pricing from "../../helpers/pricing";
import Sortable from "sortablejs";
import { initTooltips } from "flowbite";
import { useSelectionStore } from "../../stores/selection";
import { useAuthStore } from "../../stores/auth";
import { cloneDeep } from "lodash";

const projectStore = useProjectStore();
const { project } = storeToRefs(projectStore);
const selectionStore = useSelectionStore();
const { selectionInformation } = storeToRefs(selectionStore);
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const dataTable = ref();

const props = defineProps(["items", "gridColumns", "loading", "pagination"]);

const createSortable = (el, options, vnode) => {
  return Sortable.create(el, { ...options });
};

let table = null;

const vSortable = {
  beforeMount(el, binding, vnode) {
    table = el;
    table._sortable = createSortable(
      table.querySelector("tbody"),
      binding.value,
      vnode
    );
  },
};

const state = reactive({
  render: true,
});

const projectCurrency = computed(() => {
  return project.value.currencyOptions.find(
    (c) => c.value == project.value.currency
  );
});

const accessoryGridColumns = computed(() => {
  let columns = [
    {
      value: "productText",
      text: "Model",
      sortable: true,
      width: 278,
    },
    {
      value: "reference",
      text: "Reference",
      sortable: true,
      width: 200,
    },
    {
      value: "quantity",
      text: "Quantity",
      sortable: true,
      width: 140,
    },
    {
      value: "cost",
      text: `Cost Price (${projectCurrency.value.value})`,
      sortable: true,
      width: 140,
    },
    {
      value: "price",
      text: `Unit Price (${projectCurrency.value.value})`,
      sortable: true,
      width: 140,
    },
    {
      value: "totalPrice",
      text: `Total Price (${projectCurrency.value.value})`,
      sortable: true,
      width: 150,
    },
  ];
  if (!user.value.userGroup.permissions.pricing.showCostPricing) {
    columns = columns.filter((c) => c.value != "cost");
  }
  if (!user.value.userGroup.permissions.pricing.showSalePricing) {
    columns = columns.filter(
      (c) => c.value != "price" && c.value != "totalPrice"
    );
  }

  return columns;
});

const emits = defineEmits([
  "actions-view-clicked",
  "actions-edit-clicked",
  "actions-delete-clicked",
  "increase-accessory-quantity",
  "decrease-accessory-quantity",
  "increase-item-quantity",
  "decrease-item-quantity",
  "set-item-quantity",
  "set-item-fan-price",
  "set-item-motor-price",
  "accessory-reference-changed",
  "item-reference-changed",
  "item-sorted",
  "item-selected",
]);

const rowClicked = (item) => {
  props.items.forEach((i) => (i.selected = i.id == item.id && !item.selected));
  emits("item-selected", item);
};

const sortableOptions = {
  chosenClass: "is-selected",
  handle: ".row_order_handle",
  animation: 150,
  onEnd(evt) {
    emits("item-sorted", evt);
  },
};

const getColumnSlotName = (col) => {
  return "header-" + col.value;
};

const serverItemsLength = ref(0);

const serverOptions = ref({
  page: 1,
  rowsPerPage: 5,
});

const loading = ref(false);

const itemAccessories = (item) => {
  return item.accessories.filter((a) => a.quantity > 0);
};

const showAccessoriesTable = (item) => {
  return item.accessories.some((a) => a.quantity > 0);
};

const getTotalItemPriceWithDiscount = (item, qty) => {
  qty = qty ? qty : 1;
  if (qty != undefined) {
    item = cloneDeep(item);
    item.quantity = qty;
  }
  return pricing.toCurrencyString(
    pricing.getTotalItemPriceWithDiscount(item, project.value) *
      projectCurrency.value.conversionRate,
    projectCurrency.value.value
  );
};

const getItemUnitPriceWithDiscount = (item) => {
  return pricing.toCurrencyString(
    pricing.getItemUnitPriceWithDiscount(item, project.value) *
      projectCurrency.value.conversionRate,
    projectCurrency.value
  );
};

const getAccessoryDrawing = (accessory) => {
  return accessory.drawingFilename && accessory.drawingFilename.length
    ? `${window.location.origin}/assets/images/drawings/accessories/${accessory.drawingFilename}`
    : "#";
};

const getAccessoryImage = (accessory) => {
  return accessory.imageFileName && accessory.imageFileName.length
    ? `${window.location.origin}/assets/images/products/accessories/${accessory.imageFileName}`
    : "https://picsum.photos/128";
};

// const bodyRowClassNameFunction = (item) => {
//   if (item.accessories.filter((i) => i.quantity > 0) > 0) {
//     return "hide_expand";
//   }
// };

onMounted(() => {
  initTooltips();
});
</script>

<style lang="scss">
#project-items-table {
  thead tr th:first-of-type,
  tbody tr td.can-expand {
    background-color: white;
  }
}

.accessories-table {
  width: fit-content !important;
  .vue3-easy-data-table__main {
    height: fit-content;
    min-height: auto;
  }
}

.row_order_handle:hover {
  cursor: grab;
}

.row_order_handle.grabbed {
  cursor: grabbing;
}
</style>
